import React from 'react';
import { LocationData } from '../MerchantHeatmap.data';
import { PremiumArrowDownIcon, PremiumArrowUpIcon, PremiumInfoCircleIcon, PremiumMedalIcon, PremiumUserIcon } from './PremiumHeatmapIcons';

interface ActivityLevelsPanelProps {
    locations: LocationData[];
}

type Trend = { direction: 'up' | 'down' | 'neutral'; valueText: string };

const getTrendFromGrowth = (growth: number | undefined): Trend => {
    if (growth === undefined || growth === null) {
        return { direction: 'neutral', valueText: 'No data' };
    }
    if (growth > 0) {
        return { direction: 'up', valueText: `${growth}% growth` };
    } else if (growth < 0) {
        return { direction: 'down', valueText: `${Math.abs(growth)}% decline` };
    }
    return { direction: 'neutral', valueText: 'No change' };
};

const formatDemographics = (ageRange: string | undefined): string => {
    if (!ageRange || ageRange.trim() === '') {
        return 'All ages';
    }
    return `Ages ${ageRange}`;
};

export const ActivityLevelsPanel: React.FC<ActivityLevelsPanelProps> = ({ locations }) => {
    // Filter locations that have top performing coupon and sort by redemptions
    const sortedLocations = [...locations]
        .filter(loc => loc.top_coupon && loc.top_coupon !== 'N/A' && loc.top_coupon.trim() !== '')
        .sort((a, b) => b.redemptions - a.redemptions);
    
    // Show all locations with top performing coupons
    const items = sortedLocations;

    return (
        <div className="w-full h-full bg-white dark:bg-gray-800 overflow-hidden rounded-[16px] outline outline-1 outline-[#E5E7EB] dark:outline-gray-700 outline-offset-[-1px] flex flex-col min-h-0">
            <div className="p-[20px] flex items-center justify-between shrink-0">
                <div className="text-[#111827] dark:text-white text-[15px] font-normal leading-[28px]" style={{ fontFamily: 'Nunito' }}>
                    Activity Levels
                </div>
                <div className="w-[20px] h-[16px] flex items-center justify-center">
                    <PremiumInfoCircleIcon size={16} className="text-[#4B5563] dark:text-gray-300" />
                </div>
            </div>

            {/* Scrollable list area to match fixed height beside the map */}
            <div className="px-[15px] pb-[15px] flex-1 min-h-0 overflow-y-auto flex flex-col gap-[16px]">
                {items.map((loc, idx) => {
                    const trend = getTrendFromGrowth(loc.growth_percentage);
                    const trendColor = trend.direction === 'up' ? '#00A971' : trend.direction === 'down' ? '#EF4444' : '#6B7280';

                    return (
                        <div
                            key={loc.id}
                            className="p-[19.8px] bg-white dark:bg-gray-800 rounded-[16px] outline outline-1 outline-[#E5E7EB] dark:outline-gray-700 outline-offset-[-1px]"
                            style={{ boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)' }}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-[#111827] dark:text-white text-[18px] font-normal leading-[28px]" style={{ fontFamily: 'Nunito' }}>
                                    {loc.name}
                                </div>
                                <div className="w-[31px] h-[31px] bg-[#5EC269] rounded-[26843500px] flex items-center justify-center">
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                        <path d="M2 5.2L4.1 7.3L8 3.4" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            </div>

                            <div className="mt-[12px] flex items-center gap-[8px]">
                                <div className="w-[15px] h-[15px] overflow-hidden">
                                    {trend.direction === 'up' ? (
                                        <PremiumArrowUpIcon size={15} color={trendColor} />
                                    ) : trend.direction === 'down' ? (
                                        <PremiumArrowDownIcon size={15} color={trendColor} />
                                    ) : (
                                        <div style={{ width: '15px', height: '2px', backgroundColor: trendColor, margin: '6.5px 0' }} />
                                    )}
                                </div>
                                <div className="text-[14px] font-normal leading-[20px]" style={{ fontFamily: 'Nunito', color: trendColor }}>
                                    {trend.valueText}
                                </div>
                            </div>

                            <div className="mt-[12px]">
                                <div className="text-[#004232] dark:text-emerald-400 text-[23px] font-normal leading-[28px]" style={{ fontFamily: 'Nunito' }}>
                                    {loc.redemptions}
                                </div>
                                <div className="text-[#364153] dark:text-gray-300 text-[14px] font-normal leading-[20px]" style={{ fontFamily: 'Nunito' }}>
                                    Total redemptions
                                </div>
                            </div>

                            <div className="mt-[20px] pt-[20px]" style={{ borderTop: '0.8px solid #E5E7EB' }}>
                                <div className="flex items-start gap-[8px]">
                                    <div className="w-[16px] h-[18px] pt-[2px]">
                                        <PremiumMedalIcon size={16} className="text-[#00C49A]" />
                                    </div>
                                    <div className="flex flex-col gap-[2px]">
                                        <div className="text-[#6B7280] dark:text-gray-400 text-[11px] font-normal leading-[14px]" style={{ fontFamily: 'Nunito' }}>
                                            Top Performing Coupon
                                        </div>
                                        <div className="text-[#111827] dark:text-white text-[14px] font-normal leading-[18px]" style={{ fontFamily: 'Nunito' }}>
                                            {loc.top_coupon}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-[12px] flex items-start gap-[8px]">
                                    <div className="w-[16px] h-[18px] pt-[2px]">
                                        <PremiumUserIcon size={16} className="text-[#4E80EE]" />
                                    </div>
                                    <div className="flex flex-col gap-[2px]">
                                        <div className="text-[#6B7280] dark:text-gray-400 text-[11px] font-normal leading-[14px]" style={{ fontFamily: 'Nunito' }}>
                                            Demographics
                                        </div>
                                        <div className="text-[#4B5563] dark:text-gray-300 text-[12px] font-normal leading-[16px]" style={{ fontFamily: 'Nunito' }}>
                                            {formatDemographics(loc.demographics)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

